
import { create } from 'zustand';
import { CompanyPerks } from '../types';
import { supabase } from '@/integrations/supabase/client';

interface CompanyPerksStore {
  perks: CompanyPerks | null;
  isLoading: boolean;
  error: string | null;
  fetchPerks: (companyId: string) => Promise<CompanyPerks | null>;
  savePerks: (perks: Partial<CompanyPerks> & { companyId: string }) => Promise<boolean>;
}

export const useCompanyPerksStore = create<CompanyPerksStore>((set, get) => ({
  perks: null,
  isLoading: false,
  error: null,
  
  fetchPerks: async (companyId: string) => {
    try {
      set({ isLoading: true, error: null });
      console.log(`[CompanyPerks] Fetching perks for company: ${companyId}`);
      
      const { data, error } = await supabase
        .from('company_perks')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      
      if (error) {
        console.error('[CompanyPerks] Error fetching perks:', error);
        set({ error: error.message, isLoading: false });
        return null;
      }
      
      // No perks found for this company
      if (!data) {
        console.log('[CompanyPerks] No perks found for company');
        set({ perks: null, isLoading: false });
        return null;
      }
      
      // Format the response
      const formattedPerks: CompanyPerks = {
        id: data.id,
        companyId: data.company_id,
        perk1: data.perk1 || undefined,
        perk2: data.perk2 || undefined,
        perk3: data.perk3 || undefined,
        perk4: data.perk4 || undefined,
        perk5: data.perk5 || undefined,
        createdAt: new Date(data.created_at).getTime(),
      };
      
      console.log('[CompanyPerks] Successfully fetched perks:', formattedPerks);
      set({ perks: formattedPerks, isLoading: false });
      return formattedPerks;
    } catch (error: any) {
      console.error('[CompanyPerks] Unexpected error in fetchPerks:', error);
      set({ error: error.message, isLoading: false });
      return null;
    }
  },
  
  savePerks: async (perks) => {
    try {
      set({ isLoading: true, error: null });
      const { perks: currentPerks } = get();
      
      // Check if user is authenticated and has admin privileges
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Authentication required to save perks');
      }
      
      if (!user.email?.endsWith('@redflags.au')) {
        throw new Error('Admin privileges required to save perks');
      }
      
      console.log('[CompanyPerks] Current perks state:', currentPerks);
      
      // Format the perks data for database
      const perksData = {
        company_id: perks.companyId,
        perk1: perks.perk1 || null,
        perk2: perks.perk2 || null,
        perk3: perks.perk3 || null,
        perk4: perks.perk4 || null,
        perk5: perks.perk5 || null
      };
      
      let success = false;
      
      if (currentPerks?.id) {
        // Update existing perks
        console.log('[CompanyPerks] Updating existing perks:', perksData);
        const { error } = await supabase
          .from('company_perks')
          .update(perksData)
          .eq('id', currentPerks.id);
          
        if (error) {
          console.error('[CompanyPerks] Error updating perks:', error);
          throw new Error(`Failed to update perks: ${error.message}`);
        }
        
        success = true;
      } else {
        // Create new perks
        console.log('[CompanyPerks] Creating new perks:', perksData);
        const { data, error } = await supabase
          .from('company_perks')
          .insert([perksData])
          .select()
          .maybeSingle();
          
        if (error) {
          console.error('[CompanyPerks] Error creating perks:', error);
          throw new Error(`Failed to create perks: ${error.message}`);
        }
        
        if (data) {
          const newPerks: CompanyPerks = {
            id: data.id,
            companyId: data.company_id,
            perk1: data.perk1 || undefined,
            perk2: data.perk2 || undefined,
            perk3: data.perk3 || undefined,
            perk4: data.perk4 || undefined,
            perk5: data.perk5 || undefined,
            createdAt: new Date(data.created_at).getTime(),
          };
          
          set({ perks: newPerks });
        }
        
        success = true;
      }
      
      console.log('[CompanyPerks] Operation successful:', success);
      set({ isLoading: false });
      return success;
    } catch (error: any) {
      console.error('[CompanyPerks] Unexpected error in savePerks:', error);
      set({ error: error.message, isLoading: false });
      return false;
    }
  }
}));
